import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import sanityClient from "../../sanityClient";
import styled from "styled-components";
import LazyLoad from "react-lazyload";
import { Helmet } from "react-helmet";

const EventDetailContainer = styled.div`
  max-width: 800px;
  margin: 40px auto;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 15px;
    margin: 30px auto;
  }

  @media (max-width: 480px) {
    padding: 10px;
    margin: 20px auto;
  }
`;

const EventImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const EventTitle = styled.h1`
  font-size: 2.5em;
  margin-bottom: 20px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 2em;
  }

  @media (max-width: 480px) {
    font-size: 1.8em;
  }
`;

const EventMeta = styled.div`
  font-size: 1em;
  color: #666;
  margin-bottom: 20px;
  text-align: center;
`;

const EventDescription = styled.div`
  font-size: 1.2em;
  line-height: 1.8;
  color: #333;
  margin-bottom: 20px;

  p {
    margin-bottom: 20px;
  }

  blockquote {
    font-style: italic;
    margin: 20px 0;
    padding: 10px 20px;
    background-color: #e9e9e9;
    border-left: 5px solid var(--hhb-primary);
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin-bottom: 20px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  justify-content: center;
`;

const ActionButton = styled.a`
  display: inline-block;
  background-color: var(--hhb-primary);
  color: var(--hhb-white);
  padding: 10px 20px;
  font-size: 16px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  text-align: center;
  text-decoration: none;

  &:hover {
    background-color: var(--hhb-darkblue);
  }
`;

const RecentEventsContainer = styled.div`
  margin-top: 40px;
`;

const RecentEventsTitle = styled.h2`
  font-size: 1.8em;
  margin-bottom: 20px;
  text-align: center;
`;

const RecentEventsList = styled.ul`
  list-style: none;
  text-align: center;
  padding: 0;
`;

const RecentEventItem = styled.li`
  margin-bottom: 20px;
`;

const RecentEventLink = styled(Link)`
  text-decoration: none;
  color: var(--hhb-primary);
  font-size: 1.2em;

  &:hover {
    text-decoration: underline;
  }
`;

const SeeAllLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 20px;
  font-size: 1.2em;
  font-weight: bold;
  color: var(--hhb-primary);
`;

const LoadingMessage = styled.div`
  text-align: center;
  font-size: 1.5em;
  margin-top: 20px;
`;

const EventDetail = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "event" && _id == $id]{
          title,
          description,
          startDate,
          endDate,
          location,
          address,
          actionButtonText,
          actionButtonLink,
          "imageUrl": image.asset->url
        }`,
        { id }
      )
      .then((data) => setEvent(data[0]))
      .catch(console.error);
  }, [id]);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "event" && _id != $id && endDate > now()] | order(startDate asc)[0..2]{
          _id,
          title
        }`,
        { id }
      )
      .then((data) => setRecentEvents(data))
      .catch(console.error);
  }, [id]);

  if (!event) return <LoadingMessage>Loading...</LoadingMessage>;

  return (
    <>
      <Helmet>
        <title>{event.title} - Habitat for Humanity Buffalo Event</title>
        <meta name="description" content={event.description} />
      </Helmet>
      <EventDetailContainer>
        {event.imageUrl && (
          <LazyLoad height={400} offset={100}>
            <EventImage src={event.imageUrl} alt={event.title} />
          </LazyLoad>
        )}
        <EventTitle>{event.title}</EventTitle>
        <EventMeta>
          {event.startDate && (
            <div>
              <strong>Date:</strong>{" "}
              {new Date(event.startDate).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}{" "}
              from{" "}
              {new Date(event.startDate).toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })}{" "}
              to{" "}
              {new Date(event.endDate).toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })}
            </div>
          )}
          {event.location && (
            <div>
              <strong>Location:</strong> {event.location}
            </div>
          )}
          {event.address && (
            <div>
              <strong>Address:</strong> {event.address}
            </div>
          )}
        </EventMeta>

        <EventDescription>
          {event.description.split("\n").map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </EventDescription>

        <ButtonContainer>
          {event.actionButtonLink && (
            <ActionButton href={event.actionButtonLink} target="_blank">
              {event.actionButtonText || "Learn More"}
            </ActionButton>
          )}
        </ButtonContainer>

        <RecentEventsContainer>
          <RecentEventsTitle>Recent Events</RecentEventsTitle>
          <RecentEventsList>
            {recentEvents.map((recentEvent) => (
              <RecentEventItem key={recentEvent._id}>
                <RecentEventLink to={`/get-involved/events/${recentEvent._id}`}>
                  {recentEvent.title}
                </RecentEventLink>
              </RecentEventItem>
            ))}
          </RecentEventsList>
          <SeeAllLink to="/get-involved/events">See All Events</SeeAllLink>
        </RecentEventsContainer>
      </EventDetailContainer>
    </>
  );
};

export default EventDetail;
